import { Button } from "@/components/ui/button";
import { Link, useRouter } from "@tanstack/react-router";
import { getRedirectPath } from "@/lib/get-redirect-path";
import { useAuth } from "@/auth/use-auth";
import { ArrowLeft, TriangleAlert } from "lucide-react";
import { FeedbackForm } from "./feedback-form";

/**
 * Generic error component for the catchall (404) and default error boundary.
 *
 * @param title The title to display for the error.
 * @param text The description or message of the error.
 * @returns A JSX element representing the error message.
 */
export default function ErrorComponent({
  title,
  text,
}: {
  title: string;
  text: string;
}) {
  const { authUser } = useAuth();
  const redirectPath = getRedirectPath(authUser);

  const router = useRouter();
  return (
    <div className="flex min-h-[70svh] w-full items-center justify-center p-4">
      <div className="flex max-w-md flex-col items-center gap-2 text-center">
        <div className="bg-muted mb-2 flex size-14 items-center justify-center rounded-full">
          <TriangleAlert className="text-muted-foreground size-7" />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        <p className="text-muted-foreground">{text}</p>

        <div className="mt-4 flex items-center justify-center gap-3">
          <Button onClick={() => router.history.back()}>
            <ArrowLeft />
            Go Back
          </Button>
          <Button asChild variant={"outline"}>
            <Link to={redirectPath}>Return Home</Link>
          </Button>
        </div>
        <FeedbackForm
          isBugReport={true}
          title={title}
          message={text}
          trigger={
            <Button variant="link" className="text-muted-foreground text-sm">
              Report this issue
            </Button>
          }
        />
      </div>
    </div>
  );
}
